import { defaultTo, gt, isNil, slice } from 'ramda';
import { isNilOrEmpty } from 'ramda-adjunct';

const toPositiveWidth = value => {
  const width = Number(value);
  return gt(width, 0) ? width : null;
};

const createMeasureText = (font) => {
  if (typeof document === 'undefined' || isNil(document.createElement)) return null;

  const canvas = document.createElement('canvas');
  if (isNil(canvas) || isNil(canvas.getContext)) return null;

  const canvasContext2d = canvas.getContext('2d');
  if (isNil(canvasContext2d) || isNil(canvasContext2d.measureText)) return null;

  canvasContext2d.font = font;
  return text => toPositiveWidth(canvasContext2d.measureText(defaultTo('', text)).width) || 0;
};

const truncateWithHighlight = (text, maxWidth, font) => {
  const safeText = defaultTo('', text);
  const maxAllowedWidth = toPositiveWidth(maxWidth);

  if (isNilOrEmpty(safeText) || isNil(maxAllowedWidth) || isNilOrEmpty(font)) {
    return { truncated: safeText, wasTruncated: false };
  }

  const measureText = createMeasureText(font);
  if (isNil(measureText)) {
    return { truncated: safeText, wasTruncated: false };
  }

  const fullTextWidth = measureText(safeText);
  if (isNil(fullTextWidth) || fullTextWidth <= maxAllowedWidth) {
    return { truncated: safeText, wasTruncated: false };
  }

  const ellipsis = '…';
  const ellipsisTextWidth = measureText(ellipsis);
  if (isNil(ellipsisTextWidth)) {
    return { truncated: safeText, wasTruncated: false };
  }

  const availablePrefixWidth = maxAllowedWidth - ellipsisTextWidth;
  if (availablePrefixWidth <= 0) {
    return {
      truncated: ellipsis,
      wasTruncated: true
    };
  }

  const measurePrefixWidth = prefixLength => measureText(slice(0, prefixLength, safeText));

  let minPrefixLength = 0;
  let maxPrefixLength = safeText.length;
  while (minPrefixLength < maxPrefixLength) {
    const testedPrefixLength = Math.ceil((minPrefixLength + maxPrefixLength) / 2);
    const testedPrefixWidth = measurePrefixWidth(testedPrefixLength);

    if (isNil(testedPrefixWidth)) {
      return { truncated: safeText, wasTruncated: false };
    }

    if (testedPrefixWidth <= availablePrefixWidth) {
      minPrefixLength = testedPrefixLength;
    } else {
      maxPrefixLength = testedPrefixLength - 1;
    }
  }

  return {
    truncated: slice(0, minPrefixLength, safeText) + ellipsis,
    wasTruncated: true
  };
};

export default truncateWithHighlight;
